import { z } from 'zod';

import { idSchema, isoDateTimeSchema, score10Schema } from '../common';
import {
  commentJobStatusSchema,
  commentRejectionReasonSchema,
  postIntentSchema,
  postSourceTypeSchema,
} from '../enums';

/**
 * `/stats/*` DTOs — the dashboard charts.
 *
 * Every series is bucketed in the workspace timezone, server-side, so two
 * clients in different zones cannot disagree about which day a post belongs to.
 * Scores are reported on the 0.0-10.0 display scale here: these responses feed
 * charts, never the scoring pipeline.
 */

/** Calendar day in the workspace timezone, `YYYY-MM-DD`. */
export const dateBucketSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Định dạng phải là YYYY-MM-DD');

export const STATS_RANGES = ['24h', '7d', '30d', '90d', 'custom'] as const;
export const statsRangeSchema = z.enum(STATS_RANGES);
export type StatsRange = (typeof STATS_RANGES)[number];

export const STATS_GRANULARITIES = ['hour', 'day', 'week'] as const;
export const statsGranularitySchema = z.enum(STATS_GRANULARITIES);
export type StatsGranularity = (typeof STATS_GRANULARITIES)[number];

/**
 * Base query shared by every statistics endpoint. Kept as a plain object (no
 * refinement) so endpoint-specific queries can `.extend()` it; the custom-range
 * rule is applied afterwards by `checkCustomRangeBounds`.
 */
export const statsQueryBaseSchema = z.object({
  projectId: idSchema.optional(),
  range: statsRangeSchema.default('30d'),
  /** Required when `range === 'custom'`, ignored otherwise. */
  from: isoDateTimeSchema.optional(),
  to: isoDateTimeSchema.optional(),
  granularity: statsGranularitySchema.default('day'),
});

interface StatsRangeBounds {
  range: StatsRange;
  from?: string;
  to?: string;
}

/** Shared refinement: a custom range must actually carry its bounds. */
function checkCustomRangeBounds(value: StatsRangeBounds, ctx: z.RefinementCtx): void {
  if (value.range !== 'custom') return;
  if (value.from === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['from'],
      message: 'range=custom yêu cầu from',
    });
  }
  if (value.to === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['to'],
      message: 'range=custom yêu cầu to',
    });
  }
  if (value.from !== undefined && value.to !== undefined && value.from > value.to) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['to'],
      message: 'to phải sau from',
    });
  }
}

export const statsQuerySchema = statsQueryBaseSchema.superRefine(checkCustomRangeBounds);
export type StatsQuery = z.infer<typeof statsQuerySchema>;

/** Echoed on every response so a chart can label the window it is drawing. */
export const statsWindowSchema = z.object({
  from: isoDateTimeSchema,
  to: isoDateTimeSchema,
  granularity: statsGranularitySchema,
  timezone: z.string(),
});
export type StatsWindow = z.infer<typeof statsWindowSchema>;

// ---------------------------------------------------------------------------
// GET /stats/overview
// ---------------------------------------------------------------------------

export const statsOverviewQuerySchema = statsQueryBaseSchema.superRefine(checkCustomRangeBounds);
export type StatsOverviewQuery = z.infer<typeof statsOverviewQuerySchema>;

export const statsOverviewResponseSchema = z.object({
  window: statsWindowSchema,
  postsDiscovered: z.number().int().min(0),
  postsDeduplicated: z.number().int().min(0),
  highScorePostCount: z.number().int().min(0),
  averageScore10: score10Schema.nullable(),
  searchCyclesCompleted: z.number().int().min(0),
  searchCyclesFailed: z.number().int().min(0),
  commentsSent: z.number().int().min(0),
  agentsOnline: z.number().int().min(0),
  projectsRunning: z.number().int().min(0),
});
export type StatsOverviewResponse = z.infer<typeof statsOverviewResponseSchema>;

// ---------------------------------------------------------------------------
// GET /stats/posts-per-day
// ---------------------------------------------------------------------------

export const postsPerDayQuerySchema = statsQueryBaseSchema
  .extend({
    keywordId: idSchema.optional(),
  })
  .superRefine(checkCustomRangeBounds);
export type PostsPerDayQuery = z.infer<typeof postsPerDayQuerySchema>;

export const postsPerDayPointSchema = z.object({
  bucket: dateBucketSchema,
  /** New post matches created in this bucket. */
  postCount: z.number().int().min(0),
  /** Posts recognised as already known — the dedup pipeline's visible output. */
  duplicateCount: z.number().int().min(0),
  highScoreCount: z.number().int().min(0),
  commentedCount: z.number().int().min(0),
  averageScore10: score10Schema.nullable(),
});
export type PostsPerDayPoint = z.infer<typeof postsPerDayPointSchema>;

export const postsPerDayResponseSchema = z.object({
  window: statsWindowSchema,
  series: z.array(postsPerDayPointSchema),
  total: z.number().int().min(0),
});
export type PostsPerDayResponse = z.infer<typeof postsPerDayResponseSchema>;

// ---------------------------------------------------------------------------
// GET /stats/score-distribution
// ---------------------------------------------------------------------------

export const scoreDistributionQuerySchema = statsQueryBaseSchema
  .extend({
    /** Width of one histogram bin on the display scale. */
    bucketSize10: z.coerce.number().min(0.5).max(5).default(1),
  })
  .superRefine(checkCustomRangeBounds);
export type ScoreDistributionQuery = z.infer<typeof scoreDistributionQuerySchema>;

export const scoreDistributionBucketSchema = z.object({
  /** Half-open bin `[fromScore10, toScore10)`, except the last which includes 10. */
  fromScore10: score10Schema,
  toScore10: score10Schema,
  label: z.string(),
  count: z.number().int().min(0),
  percentage: z.number().min(0).max(100),
});
export type ScoreDistributionBucket = z.infer<typeof scoreDistributionBucketSchema>;

export const scoreDistributionResponseSchema = z.object({
  window: statsWindowSchema,
  buckets: z.array(scoreDistributionBucketSchema),
  total: z.number().int().min(0),
  averageScore10: score10Schema.nullable(),
  medianScore10: score10Schema.nullable(),
});
export type ScoreDistributionResponse = z.infer<typeof scoreDistributionResponseSchema>;

// ---------------------------------------------------------------------------
// GET /stats/by-source-type
// ---------------------------------------------------------------------------

export const postsBySourceTypeQuerySchema =
  statsQueryBaseSchema.superRefine(checkCustomRangeBounds);
export type PostsBySourceTypeQuery = z.infer<typeof postsBySourceTypeQuerySchema>;

export const postsBySourceTypeItemSchema = z.object({
  sourceType: postSourceTypeSchema,
  postCount: z.number().int().min(0),
  percentage: z.number().min(0).max(100),
  averageScore10: score10Schema.nullable(),
  highScoreCount: z.number().int().min(0),
});
export type PostsBySourceTypeItem = z.infer<typeof postsBySourceTypeItemSchema>;

export const postsBySourceTypeResponseSchema = z.object({
  window: statsWindowSchema,
  items: z.array(postsBySourceTypeItemSchema),
  total: z.number().int().min(0),
});
export type PostsBySourceTypeResponse = z.infer<typeof postsBySourceTypeResponseSchema>;

// ---------------------------------------------------------------------------
// GET /stats/by-keyword
// ---------------------------------------------------------------------------

export const postsByKeywordQuerySchema = statsQueryBaseSchema
  .extend({
    limit: z.coerce.number().int().min(1).max(100).default(20),
  })
  .superRefine(checkCustomRangeBounds);
export type PostsByKeywordQuery = z.infer<typeof postsByKeywordQuerySchema>;

export const postsByKeywordItemSchema = z.object({
  keywordId: idSchema,
  term: z.string(),
  projectId: idSchema,
  postCount: z.number().int().min(0),
  highScoreCount: z.number().int().min(0),
  averageScore10: score10Schema.nullable(),
  commentCount: z.number().int().min(0),
  /** Dominant intent across the matched posts; null before AI scoring runs. */
  topIntent: postIntentSchema.nullable(),
});
export type PostsByKeywordItem = z.infer<typeof postsByKeywordItemSchema>;

export const postsByKeywordResponseSchema = z.object({
  window: statsWindowSchema,
  items: z.array(postsByKeywordItemSchema),
  total: z.number().int().min(0),
});
export type PostsByKeywordResponse = z.infer<typeof postsByKeywordResponseSchema>;

// ---------------------------------------------------------------------------
// GET /stats/comment-outcomes
// ---------------------------------------------------------------------------

export const commentOutcomesQuerySchema =
  statsQueryBaseSchema.superRefine(checkCustomRangeBounds);
export type CommentOutcomesQuery = z.infer<typeof commentOutcomesQuerySchema>;

export const commentOutcomeByStatusSchema = z.object({
  status: commentJobStatusSchema,
  count: z.number().int().min(0),
});
export type CommentOutcomeByStatus = z.infer<typeof commentOutcomeByStatusSchema>;

/**
 * Why comments did not go out. This is the most operationally useful chart in
 * the product: a spike in `RATE_LIMIT_*` means the limiter is doing its job,
 * whereas a spike in `SENSITIVE_WORD` means the templates need work.
 */
export const commentRejectionBreakdownSchema = z.object({
  reason: commentRejectionReasonSchema,
  count: z.number().int().min(0),
});
export type CommentRejectionBreakdown = z.infer<typeof commentRejectionBreakdownSchema>;

export const commentOutcomePointSchema = z.object({
  bucket: dateBucketSchema,
  sent: z.number().int().min(0),
  succeeded: z.number().int().min(0),
  failed: z.number().int().min(0),
  rejected: z.number().int().min(0),
});
export type CommentOutcomePoint = z.infer<typeof commentOutcomePointSchema>;

export const commentOutcomesResponseSchema = z.object({
  window: statsWindowSchema,
  byStatus: z.array(commentOutcomeByStatusSchema),
  rejections: z.array(commentRejectionBreakdownSchema),
  series: z.array(commentOutcomePointSchema),
  totals: z.object({
    created: z.number().int().min(0),
    sent: z.number().int().min(0),
    succeeded: z.number().int().min(0),
    failed: z.number().int().min(0),
    cancelled: z.number().int().min(0),
    awaitingApproval: z.number().int().min(0),
    /** succeeded / sent, 0.0-1.0. Null when nothing was sent in the window. */
    successRate: z.number().min(0).max(1).nullable(),
  }),
});
export type CommentOutcomesResponse = z.infer<typeof commentOutcomesResponseSchema>;
